"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import {
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  MessageSquare,
  Plus,
  Pin,
  Lock,
  Shield,
  Loader2,
  Reply,
  CheckCircle2,
  Image as ImageIcon,
  X,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useGuideThreads,
  useGuideThread,
  useCreateThread,
  useCreatePost,
  useDeleteThread,
  useDeletePost,
  useGuideVote,
  useUploadGuideImage,
  type GuideThread,
  type GuidePost,
  type ThreadAuthor,
  type GuideImage,
} from "@/hooks/useGuideDiscussion";
import { useAuth } from "@/components/auth/AuthProvider";

const MAX_IMAGES = 4;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fullDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function joinedDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Small primitives ────────────────────────────────────────────────────────

function ProfileAvatar({ author, size = 96 }: { author: ThreadAuthor; size?: number }) {
  const dim = { width: size, height: size };
  if (author.avatar_url) {
    return (
      <img
        src={author.avatar_url}
        alt={author.name}
        style={dim}
        className="rounded-md object-cover ring-1 ring-border/50"
      />
    );
  }
  return (
    <div
      style={dim}
      className="grid place-items-center rounded-md bg-primary/15 text-primary font-semibold ring-1 ring-border/50"
    >
      <span className="text-xl">{(author.name ?? "?").slice(0, 2).toUpperCase()}</span>
    </div>
  );
}

function VoteButtons({
  targetType,
  targetId,
  voteCount,
  userVote,
  disabled,
}: {
  targetType: "thread" | "post";
  targetId: string;
  voteCount: number;
  userVote: -1 | 0 | 1;
  disabled?: boolean;
}) {
  const vote = useGuideVote();

  function handleVote(value: -1 | 0 | 1) {
    if (disabled) return;
    const next = userVote === value ? 0 : value;
    vote.mutate({ target_type: targetType, target_id: targetId, value: next as -1 | 0 | 1 });
  }

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-1 py-0.5">
      <button
        onClick={() => handleVote(1)}
        disabled={disabled || vote.isPending}
        className={`rounded-full p-1 transition-colors ${
          userVote === 1 ? "text-primary" : "text-muted-foreground hover:text-foreground"
        } disabled:opacity-40`}
        aria-label="Upvote"
      >
        <ChevronUp className="h-3.5 w-3.5" />
      </button>
      <span
        className={`min-w-[1.5ch] text-center text-xs font-semibold tabular-nums ${
          voteCount > 0 ? "text-primary" : voteCount < 0 ? "text-destructive" : "text-muted-foreground"
        }`}
      >
        {voteCount}
      </span>
      <button
        onClick={() => handleVote(-1)}
        disabled={disabled || vote.isPending}
        className={`rounded-full p-1 transition-colors ${
          userVote === -1 ? "text-destructive" : "text-muted-foreground hover:text-foreground"
        } disabled:opacity-40`}
        aria-label="Downvote"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ImageGallery({ urls }: { urls: string[] }) {
  if (!urls.length) return null;
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
      {urls.map((url) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="group block overflow-hidden rounded-lg border border-border/60 bg-muted/30"
        >
          <img
            src={url}
            alt=""
            loading="lazy"
            className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </a>
      ))}
    </div>
  );
}

// ─── Image picker ────────────────────────────────────────────────────────────

function ImagePicker({
  images,
  onChange,
  disabled,
}: {
  images: GuideImage[];
  onChange: (next: GuideImage[]) => void;
  disabled?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadGuideImage();
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    setError(null);
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      setError(`You can attach up to ${MAX_IMAGES} images.`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    const uploaded: GuideImage[] = [];
    for (const file of picked) {
      try {
        const result = await upload.mutateAsync(file);
        uploaded.push(result);
      } catch (e) {
        setError((e as Error)?.message ?? "Upload failed.");
        break;
      }
    }
    if (uploaded.length) onChange([...images, ...uploaded]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || upload.isPending || images.length >= MAX_IMAGES}
          className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/30 hover:text-primary disabled:opacity-50"
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ImageIcon className="h-3.5 w-3.5" />
          )}
          {upload.isPending ? "Uploading…" : "Add image"}
        </button>
        <span className="text-[11px] text-muted-foreground">
          {images.length}/{MAX_IMAGES} · PNG, JPEG, WebP, GIF · max 5 MB each
        </span>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {images.map((img) => (
            <div key={img.path} className="relative overflow-hidden rounded-lg border border-border/60">
              <img src={img.public_url} alt="" className="h-24 w-full object-cover" />
              <button
                type="button"
                onClick={() => onChange(images.filter((x) => x.path !== img.path))}
                className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground shadow-sm hover:bg-destructive hover:text-destructive-foreground"
                aria-label="Remove image"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ─── phpBB-style post ────────────────────────────────────────────────────────

function ForumPost({
  post,
  threadTitle,
  isFirst,
  alt,
  isLoggedIn,
  canDelete,
  onDelete,
  isDeleting,
}: {
  post: GuidePost | GuideThread;
  threadTitle?: string;
  isFirst?: boolean;
  alt?: boolean;
  isLoggedIn: boolean;
  canDelete: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
}) {
  const targetType: "thread" | "post" = isFirst ? "thread" : "post";
  const isSolution = "is_solution" in post && post.is_solution;
  const imageUrls = post.image_urls ?? [];
  return (
    <article
      className={`overflow-hidden rounded-xl border ${
        isSolution ? "border-[hsl(var(--success)/0.4)]" : "border-border/60"
      } ${alt ? "bg-muted/30" : "bg-card"}`}
    >
      <div className="grid sm:grid-cols-[180px_1fr]">
        {/* Profile column */}
        <aside className="flex flex-col items-center gap-2 border-b border-border/50 bg-muted/40 px-4 py-5 sm:items-start sm:border-b-0 sm:border-r">
          <ProfileAvatar author={post.author} size={88} />
          <div className="mt-1 flex flex-col items-center gap-1 sm:items-start">
            <span className="text-sm font-semibold text-foreground">{post.author.name}</span>
            {post.author.is_admin && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                <Shield className="h-2.5 w-2.5" />
                Site Admin
              </span>
            )}
          </div>
          <dl className="mt-2 w-full space-y-0.5 border-t border-border/40 pt-3 text-[11px] leading-snug text-muted-foreground">
            <div className="flex justify-between gap-2 sm:block">
              <dt className="font-medium text-foreground/80">Posts</dt>
              <dd className="tabular-nums">{post.author.post_count}</dd>
            </div>
            <div className="flex justify-between gap-2 sm:block">
              <dt className="font-medium text-foreground/80">Joined</dt>
              <dd>{joinedDate(post.author.joined_at)}</dd>
            </div>
          </dl>
        </aside>

        {/* Body */}
        <div className="p-5">
          {isFirst && threadTitle && (
            <h3 className="mb-1 text-base font-semibold leading-snug text-foreground sm:text-lg">
              {threadTitle}
            </h3>
          )}
          {isSolution && (
            <p className="mb-2 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--success)/0.1)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">
              <CheckCircle2 className="h-3 w-3" />
              Accepted answer
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            by{" "}
            <span className="font-semibold text-foreground/90">{post.author.name}</span>
            {" » "}
            <time dateTime={post.created_at} title={fullDate(post.created_at)}>
              {fullDate(post.created_at)}
            </time>
          </p>

          <div className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground/90">
            {post.body}
          </div>

          {imageUrls.length > 0 && <ImageGallery urls={imageUrls} />}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-border/40 pt-3 text-xs text-muted-foreground">
            <span>{timeAgo(post.created_at)}</span>
            <div className="flex items-center gap-2">
              {canDelete && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive disabled:opacity-50"
                >
                  {isDeleting ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                  Delete
                </button>
              )}
              <VoteButtons
                targetType={targetType}
                targetId={post.id}
                voteCount={post.vote_count}
                userVote={post.user_vote}
                disabled={!isLoggedIn}
              />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

// ─── Topic (thread + replies) view ───────────────────────────────────────────

function TopicView({
  guideId,
  threadId,
  onBack,
  isLoggedIn,
  currentUserId,
  isAdmin,
}: {
  guideId: string;
  threadId: string;
  onBack: () => void;
  isLoggedIn: boolean;
  currentUserId: string | null;
  isAdmin: boolean;
}) {
  const [replyBody, setReplyBody] = useState("");
  const [replyImages, setReplyImages] = useState<GuideImage[]>([]);
  const { data, isLoading } = useGuideThread(guideId, threadId);
  const createPost = useCreatePost(guideId, threadId);
  const deleteThread = useDeleteThread(guideId);
  const deletePost = useDeletePost(guideId, threadId);

  async function handleReply(e: React.FormEvent) {
    e.preventDefault();
    if (!replyBody.trim()) return;
    await createPost.mutateAsync({
      body: replyBody,
      image_paths: replyImages.map((i) => i.path),
    });
    setReplyBody("");
    setReplyImages([]);
  }

  async function handleDeleteThread() {
    if (!confirm("Delete this thread and all its replies? This cannot be undone.")) return;
    await deleteThread.mutateAsync(threadId);
    onBack();
  }

  async function handleDeletePost(postId: string) {
    if (!confirm("Delete this reply? This cannot be undone.")) return;
    await deletePost.mutateAsync(postId);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">Thread not found.</div>
    );
  }

  const { thread, posts } = data;
  const totalPosts = 1 + posts.length;

  const canDeleteThread = isAdmin || (currentUserId !== null && thread.user_id === currentUserId);

  return (
    <div className="space-y-4">
      {/* Action bar (phpBB-like) */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 px-4 py-2.5 text-xs text-muted-foreground">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back to threads
        </button>
        <div className="flex items-center gap-3">
          {thread.is_pinned && (
            <span className="inline-flex items-center gap-1">
              <Pin className="h-3 w-3 text-primary" />
              Pinned
            </span>
          )}
          {thread.is_locked && (
            <span className="inline-flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Locked
            </span>
          )}
          <span>
            {totalPosts} {totalPosts === 1 ? "post" : "posts"}
          </span>
        </div>
      </div>

      <ForumPost
        post={thread}
        threadTitle={thread.title}
        isFirst
        alt={false}
        isLoggedIn={isLoggedIn}
        canDelete={canDeleteThread}
        onDelete={canDeleteThread ? handleDeleteThread : undefined}
        isDeleting={deleteThread.isPending}
      />

      {posts.map((post, idx) => {
        const canDeletePost =
          isAdmin || (currentUserId !== null && post.user_id === currentUserId);
        return (
          <ForumPost
            key={post.id}
            post={post}
            alt={idx % 2 === 0}
            isLoggedIn={isLoggedIn}
            canDelete={canDeletePost}
            onDelete={canDeletePost ? () => handleDeletePost(post.id) : undefined}
            isDeleting={deletePost.isPending && deletePost.variables === post.id}
          />
        );
      })}

      {!thread.is_locked ? (
        <div className="rounded-xl border border-border/60 bg-card p-4">
          {isLoggedIn ? (
            <form onSubmit={handleReply} className="space-y-3">
              <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Reply className="h-3.5 w-3.5" />
                Post a reply
              </label>
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Write a reply…"
                rows={4}
                maxLength={5000}
                className="w-full resize-none rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/30"
              />
              <ImagePicker images={replyImages} onChange={setReplyImages} />
              {createPost.error && (
                <p className="text-xs text-destructive">
                  {(createPost.error as Error)?.message ?? "Failed to post reply."}
                </p>
              )}
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground">{replyBody.length}/5000</p>
                <Button
                  type="submit"
                  size="sm"
                  disabled={createPost.isPending || replyBody.trim().length < 2}
                  className="rounded-full"
                >
                  {createPost.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Post Reply"
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <p className="py-2 text-center text-sm text-muted-foreground">
              <Link href="/auth" className="text-primary underline-offset-4 hover:underline">
                Sign in
              </Link>{" "}
              to reply.
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-border/60 bg-muted/30 py-3 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" />
          This thread is locked.
        </div>
      )}
    </div>
  );
}

// ─── Thread list row ─────────────────────────────────────────────────────────

function ThreadRow({
  thread,
  onOpen,
}: {
  thread: GuideThread;
  onOpen: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onOpen(thread.id)}
      className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg border border-border/60 bg-card px-4 py-3 text-left transition-colors hover:border-border hover:bg-muted/30 sm:grid-cols-[auto_1fr_auto_auto_auto]"
    >
      <span className="text-muted-foreground">
        <MessageSquare className="h-4 w-4" />
      </span>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          {thread.is_pinned && <Pin className="h-3 w-3 shrink-0 text-primary" />}
          {thread.is_locked && <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />}
          <h4 className="truncate text-sm font-semibold text-foreground hover:text-primary">
            {thread.title}
          </h4>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          by <span className="font-medium text-foreground/80">{thread.author.name}</span>
          {" » "}
          {timeAgo(thread.created_at)}
        </p>
      </div>

      <div className="hidden text-center text-xs sm:block">
        <p className="font-semibold tabular-nums text-foreground">{thread.reply_count}</p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">replies</p>
      </div>
      <div className="hidden text-center text-xs sm:block">
        <p
          className={`font-semibold tabular-nums ${
            thread.vote_count > 0
              ? "text-primary"
              : thread.vote_count < 0
                ? "text-destructive"
                : "text-foreground"
          }`}
        >
          {thread.vote_count}
        </p>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">votes</p>
      </div>

      <span className="text-xs text-muted-foreground sm:hidden">
        {thread.reply_count} {thread.reply_count === 1 ? "reply" : "replies"}
      </span>
    </button>
  );
}

// ─── New thread form (inline) ────────────────────────────────────────────────

function NewThreadForm({
  guideId,
  sectionId,
  onClose,
  onCreated,
}: {
  guideId: string;
  sectionId: string;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState<GuideImage[]>([]);
  const create = useCreateThread(guideId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const { thread } = await create.mutateAsync({
        title,
        section: sectionId,
        body,
        image_paths: images.map((i) => i.path),
      });
      onCreated(thread.id);
    } catch {
      // surfaced below
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-xl border border-border/60 bg-card p-4"
    >
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          New Thread
        </label>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Topic title…"
        maxLength={200}
        className="w-full rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/30"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Describe your question, issue, or tip… (10–5000 characters)"
        rows={5}
        maxLength={5000}
        className="w-full resize-none rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/30"
      />
      <ImagePicker images={images} onChange={setImages} />

      {create.error && (
        <p className="text-xs text-destructive">
          {(create.error as Error)?.message ?? "Failed to create thread."}
        </p>
      )}

      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground">{body.length}/5000</p>
        <Button
          type="submit"
          size="sm"
          disabled={create.isPending || title.length < 3 || body.length < 10}
          className="rounded-full"
        >
          {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post Thread"}
        </Button>
      </div>
    </form>
  );
}

// ─── Section forum (the public export) ───────────────────────────────────────

export function SectionForum({
  guideId,
  sectionId,
  sectionLabel,
}: {
  guideId: string;
  sectionId: string;
  sectionLabel: string;
}) {
  const { user, isAdmin } = useAuth();
  const isLoggedIn = !!user;
  const currentUserId = user?.id ?? null;

  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading } = useGuideThreads(guideId, {
    section: sectionId,
    sort: "new",
  });

  const threads = data?.threads ?? [];

  return (
    <div className="mt-10 rounded-2xl border border-border/50 bg-background/40 p-5 sm:p-6">
      <header className="mb-4 flex items-center justify-between gap-3 border-b border-border/40 pb-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80">
            Discussion
          </p>
          <h3 className="text-sm font-semibold text-foreground sm:text-base">
            {sectionLabel}
            {!isLoading && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                · {threads.length} {threads.length === 1 ? "thread" : "threads"}
              </span>
            )}
          </h3>
        </div>
        {!openThreadId && isLoggedIn && !showCreate && (
          <Button
            size="sm"
            onClick={() => setShowCreate(true)}
            className="rounded-full gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            New Thread
          </Button>
        )}
      </header>

      {openThreadId ? (
        <TopicView
          guideId={guideId}
          threadId={openThreadId}
          onBack={() => setOpenThreadId(null)}
          isLoggedIn={isLoggedIn}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
        />
      ) : (
        <div className="space-y-4">
          {showCreate && (
            <NewThreadForm
              guideId={guideId}
              sectionId={sectionId}
              onClose={() => setShowCreate(false)}
              onCreated={(id) => {
                setShowCreate(false);
                setOpenThreadId(id);
              }}
            />
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : threads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 py-8 text-center text-sm text-muted-foreground">
              <MessageSquare className="mx-auto mb-2 h-7 w-7 opacity-30" />
              No threads yet for {sectionLabel}.
              {isLoggedIn ? (
                <button
                  onClick={() => setShowCreate(true)}
                  className="mt-2 block w-full text-primary hover:underline"
                >
                  Start the first discussion
                </button>
              ) : (
                <p className="mt-2 text-xs">
                  <Link href="/auth" className="text-primary underline-offset-4 hover:underline">
                    Sign in
                  </Link>{" "}
                  to post the first thread.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {threads.map((thread) => (
                <ThreadRow
                  key={thread.id}
                  thread={thread}
                  onOpen={setOpenThreadId}
                />
              ))}
            </div>
          )}

          {!isLoggedIn && threads.length > 0 && (
            <p className="pt-1 text-center text-xs text-muted-foreground">
              <Link href="/auth" className="text-primary underline-offset-4 hover:underline">
                Sign in
              </Link>{" "}
              to start a thread or vote.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
